import { CaretDownOutlined, CaretRightOutlined, WarningFilled } from "@ant-design/icons"
import { Flex, Space, Table, Tooltip } from "antd"
import { useContext, useEffect, useState } from "react"
import ReactCountryFlag from "react-country-flag"
import { PositionsContext } from "../../../context/PositionsContext"
import { spaces } from "../../../utils/styles"
import { getAllKeys } from "../../../utils/utils"
import { MoneyLabel } from "../../atoms/formatters/MoneyLabel"
import { SimpleRoundedValueLabel } from "../../atoms/formatters/SimpleRoundedValueLabel"
import { PercentLabel } from "../../atoms/formatters/PercentLabel"

export const PositionsByStructureTable = () => {
  const { positions, isStructuresView, isLoading, isToExpandAll } = useContext(PositionsContext)
  const [expandedRowKeys, setExpandedRowKeys] = useState([])
  const [allKeys, setAllKeys] = useState([])

  useEffect(() => setAllKeys(getAllKeys(positions)), [positions])
  useEffect(() => setExpandedRowKeys(isToExpandAll ? allKeys : []), [isToExpandAll])

  const onExpand = (record) => {
    const keys = expandedRowKeys.includes(record.key) ? expandedRowKeys.filter((key) => key !== record.key) : [...expandedRowKeys, record.key]
    setExpandedRowKeys(keys)
  }

  if (!isStructuresView) return null

  const expandIcon = ({ expanded, record }) => {
    if (isLoading) return null

    if (record.children === undefined) return null

    const props = {
      style: { marginRight: spaces.space2, cursor: "pointer" },
      onClick: () => onExpand(record),
    }

    return expanded ? <CaretDownOutlined {...props} /> : <CaretRightOutlined {...props} />
  }

  const recursiveSum = (node, field) =>
    node.children ? node.children.reduce((sum, child) => sum + recursiveSum(child, field), node[field] || 0) : node[field]

  const REFERENCE_TO_PERCENTAGE = "value_usd"

  const totalUSD = positions.reduce((sum, position) => sum + recursiveSum(position, REFERENCE_TO_PERCENTAGE), 0)
  const totalBRL = positions.reduce((sum, position) => sum + recursiveSum(position, "value_brl"), 0)

  const columns = [
    {
      title: "",
      dataIndex: "asset",
      render: (asset, record) => {
        if (record.level === 0) return asset

        const accountRestriction = record.level === 2 && record.children?.[0]?.children?.[0]?.restrictions
        const countryCode = record.level === 2 && record.children?.[0]?.children?.[0]?.country_code
        const country = record.level === 2 && record.children?.[0]?.children?.[0]?.country

        return (
          <Space onClick={() => onExpand(record)} style={{ cursor: "pointer" }}>
            {countryCode && country && (
              <Flex style={{ gap: spaces.space1, justifyContent: "center" }}>
                <ReactCountryFlag countryCode={countryCode} style={{ width: 18 }} title={country} svg />
              </Flex>
            )}
            <b>{asset}</b>
            {accountRestriction && (
              <Tooltip title={accountRestriction}>
                <WarningFilled style={{ cursor: "pointer", fontSize: "16px", color: "orange" }} />
              </Tooltip>
            )}
          </Space>
        )
      },
    },
    {
      title: "Amount (US$)",
      dataIndex: "value_usd",
      align: "center",
      width: 150,
      render: (_, row) => <MoneyLabel value={recursiveSum(row, "value_usd")} currency="USD" />,
    },
    {
      title: "Amount (R$)",
      dataIndex: "value_brl",
      align: "center",
      width: 150,
      render: (_, row) => <MoneyLabel value={recursiveSum(row, "value_brl")} currency="BRL" />,
    },
    {
      title: "%",
      dataIndex: REFERENCE_TO_PERCENTAGE,
      align: "center",
      width: 150,
      render: (_, row) => <PercentLabel value={recursiveSum(row, REFERENCE_TO_PERCENTAGE) / totalUSD} decimalPlaces={1} />,
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      align: "center",
      width: 150,
      render: (quantity) => <SimpleRoundedValueLabel value={quantity} />,
    },
  ]

  const WIDTH = "calc(100vw - 70px)"
  const HEIGHT = "calc(100vh - 185px)"

  const Footer = () => {
    return (
      <Table.Summary fixed>
        <Table.Summary.Row className="table-summary-row">
          <Table.Summary.Cell index={0}>
            <b>Total</b>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={1}>
            <Flex style={{ justifyContent: "center", textAlign: "center" }}>
              <b>
                <MoneyLabel value={totalUSD} currency="USD" />
              </b>
            </Flex>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={2} style={{ justifyContent: "center", textAlign: "center" }}>
            <Flex style={{ justifyContent: "center", textAlign: "center" }}>
              <b>
                <MoneyLabel value={totalBRL} currency="BRL" />
              </b>
            </Flex>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={3} style={{ justifyContent: "center", textAlign: "center" }}>
            <Flex style={{ justifyContent: "center", textAlign: "center" }}>
              <b>100%</b>
            </Flex>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    )
  }

  return (
    <Table
      columns={columns}
      dataSource={positions}
      size="small"
      pagination={false}
      expandable={{ rowExpandable: true, expandIcon, expandedRowKeys, onExpand }}
      style={{ width: WIDTH, maxHeight: HEIGHT }}
      scroll={{ y: "calc(100vh - 260px)", x: "max-content" }}
      summary={Footer}
    />
  )
}
